// ═══════════════════════════════════════════════════════════
// Invoices Tab — fee estimates, invoices, payment tracking
// Supports multiple invoices per request.
// Stripe is STUBBED — no real payment processing yet.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import {
  IconPlus, IconLoader2, IconReceipt, IconCheck, IconAlertTriangle,
  IconLink, IconCopy, IconChevronDown, IconChevronUp,
} from '@tabler/icons-react';
import { formatMoney, formatDate } from '../../../../lib/format';
import {
  listInvoicesForRequest, createInvoice, updateInvoiceStatus,
  addInvoiceItem, recalcInvoice, resolveFeeWaiver,
  type Invoice, type InvoiceItem, type InvoiceStatus, type InvoiceItemType,
} from '../../../../api/invoices';
import {
  createPaymentToken, getPaymentTokenForInvoice, buildPaymentUrl,
} from '../../../../api/paymentTokens';
import { sendCorrespondence } from '../../../../api/correspondence';
import type { FoiaRequest, Requester } from '../../../../api/requests';

interface Props {
  requestId:    number;
  requesterId?: number;
  actorEmail?:  string;
  request?:     FoiaRequest;
  requester?:   Requester | null;
}

const STATUS_STYLE: Record<InvoiceStatus, { bg: string; text: string; label: string }> = {
  draft:        { bg: 'var(--cds-gray-50)', text: 'var(--color-neutral-500)', label: 'Draft'          },
  estimateOnly: { bg: 'var(--cds-yellow-50)', text: 'var(--cds-yellow-500)', label: 'Estimate Sent'  },
  sent:         { bg: 'var(--cds-blurple-50)', text: 'var(--color-primary-700)', label: 'Invoice Sent'   },
  paid:         { bg: 'var(--cds-green-50)', text: 'var(--cds-green-700)', label: 'Paid'           },
  void:         { bg: 'var(--cds-gray-50)', text: 'var(--color-neutral-500)', label: 'Void'           },
};

const ITEM_TYPES: { value: InvoiceItemType; label: string }[] = [
  { value: 'search',        label: 'Search / Retrieval' },
  { value: 'copy',          label: 'Copies'             },
  { value: 'certification', label: 'Certification'      },
  { value: 'redaction',     label: 'Redaction Review'   },
  { value: 'delivery',      label: 'Delivery / Postage' },
  { value: 'other',         label: 'Other'              },
];

// ── Send preview modal ────────────────────────────────────

function SendPreviewModal({
  invoice, sendType, request, requester, actorEmail, onConfirm, onClose,
}: {
  invoice:     Invoice;
  sendType:    'sent' | 'estimateOnly';
  request?:    FoiaRequest;
  requester?:  Requester | null;
  actorEmail?: string;
  onConfirm:   (emailBody: string, sendEmail: boolean) => Promise<void>;
  onClose:     () => void;
}) {
  const isEstimate = sendType === 'estimateOnly';
  const label  = isEstimate ? 'Estimate' : 'Invoice';
  const reqNum = request?.requestNumber ?? String(invoice.requestId);

  const defaultBody = [
    `Dear ${requester?.displayName ?? 'Requester'},`,
    '',
    isEstimate
      ? `We have reviewed your public records request #${reqNum} and are providing a fee estimate before proceeding.`
      : `Your public records request #${reqNum} has been processed. Please find the invoice details below.`,
    '',
    invoice.items.map(it => `  ${it.description}: ${formatMoney(it.total)}`).join('\n'),
    '',
    `${isEstimate ? 'Estimated Total' : 'Total Due'}: ${formatMoney(invoice.total)}`,
    '',
    isEstimate
      ? 'Please reply to confirm whether you would like to proceed. We will begin processing upon your confirmation.'
      : 'Payment is due within 30 days. Please contact us at records@example.gov with any questions.',
    '',
    'Thank you,\nRecords Office',
  ].join('\n');

  const [emailBody, setEmailBody] = useState(defaultBody);
  const [sendEmail, setSendEmail] = useState(!!requester?.email);
  const [sending,   setSending]   = useState(false);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'white', borderRadius: 4, width: 600, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--color-neutral-100)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>Send {label} — Review</h2>
          <div style={{ fontSize: 14, color: 'var(--color-neutral-500)', marginTop: 4 }}>Invoice #{invoice.recordId} · {formatMoney(invoice.total)}</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Line items preview */}
          <div style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--color-neutral-50)' }}>
                  {['Item', 'Qty', 'Unit Price', 'Total'].map((h, i) => (
                    <th key={h} style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-neutral-500)', textAlign: i === 0 ? 'left' : 'right', borderBottom: '1px solid var(--color-neutral-100)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoice.items.map(item => (
                  <tr key={item.recordId} style={{ borderTop: '1px solid var(--color-neutral-100)' }}>
                    <td style={{ padding: '7px 12px', fontSize: 12, color: 'var(--color-neutral-700)' }}>{item.description}</td>
                    <td style={{ padding: '7px 12px', fontSize: 12, textAlign: 'right', color: 'var(--color-neutral-500)' }}>{item.quantity}</td>
                    <td style={{ padding: '7px 12px', fontSize: 12, textAlign: 'right', color: 'var(--color-neutral-500)' }}>{formatMoney(item.unitPrice)}</td>
                    <td style={{ padding: '7px 12px', fontSize: 12, textAlign: 'right', fontWeight: 600 }}>{formatMoney(item.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--color-neutral-200)' }}>
                  <td colSpan={3} style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-600)' }}>Total</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 16, fontWeight: 700 }}>{formatMoney(invoice.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          {/* Email */}
          <div>
            {requester?.email ? (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', marginBottom: 10, color: 'var(--color-neutral-700)' }}>
                  <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
                  Email to <strong>{requester.email}</strong>
                </label>
                {sendEmail && (
                  <textarea className="form-input" style={{ fontSize: 14, fontFamily: 'inherit', lineHeight: 1.6, minHeight: 180, resize: 'vertical' }} value={emailBody} onChange={e => setEmailBody(e.target.value)} />
                )}
              </>
            ) : (
              <div style={{ fontSize: 14, color: 'var(--color-neutral-400)', fontStyle: 'italic' }}>No requester email on file — status will update but no email sent.</div>
            )}
          </div>
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--color-neutral-100)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary btn-sm" style={{ gap: 4 }} disabled={sending} onClick={async () => { setSending(true); try { await onConfirm(emailBody, sendEmail && !!requester?.email); } finally { setSending(false); } }}>
            {sending ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : `Send ${label} →`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mark Paid modal ───────────────────────────────────────

function MarkPaidModal({ invoice, onConfirm, onClose }: { invoice: Invoice; onConfirm: () => Promise<void>; onClose: () => void }) {
  const [saving, setSaving] = useState(false);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'white', borderRadius: 4, padding: 24, width: 380, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 8px' }}>Mark Invoice as Paid?</h2>
        <p style={{ fontSize: 14, color: 'var(--color-neutral-600)', margin: '0 0 20px', lineHeight: 1.5 }}>
          This will mark Invoice #{invoice.recordId} ({formatMoney(invoice.total)}) as paid and record today as the payment date.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" style={{ gap: 4, background: 'var(--cds-green-700)', borderColor: 'var(--cds-green-700)' }} disabled={saving}
            onClick={async () => { setSaving(true); try { await onConfirm(); } finally { setSaving(false); } }}>
            {saving ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconCheck size={12} /> Confirm Paid</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Payment Link Modal ────────────────────────────────────

function PaymentLinkModal({ invoice, paymentUrl, requester, request, onSendEmail, onClose }: {
  invoice:     Invoice;
  paymentUrl:  string;
  requester?:  Requester | null;
  request?:    FoiaRequest;
  onSendEmail: () => Promise<void>;
  onClose:     () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'white', borderRadius: 4, padding: 24, width: 480, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 6px' }}>
          Payment Link — Invoice #{invoice.recordId}
        </h2>
        <div style={{ fontSize: 14, color: 'var(--color-neutral-500)', marginBottom: 16 }}>
          {request?.requestNumber} · {formatMoney(invoice.total)} · expires in 30 days
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input className="form-input" readOnly value={paymentUrl} style={{ flex: 1, fontSize: 12, fontFamily: 'monospace', color: 'var(--color-neutral-700)' }} onFocus={e => e.target.select()} />
          <button className="btn btn-secondary btn-sm" style={{ gap: 4, flexShrink: 0 }} onClick={() => { navigator.clipboard.writeText(paymentUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
            {copied ? <><IconCheck size={12} /> Copied!</> : <><IconCopy size={12} /> Copy</>}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
          {requester?.email && (
            <button className="btn btn-primary btn-sm" style={{ gap: 4 }} onClick={onSendEmail}>
              <IconLink size={12} /> Email to {requester.email}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Single invoice card ───────────────────────────────────

function InvoiceCard({
  invoice: initialInvoice,
  requestId, request, requester, actorEmail,
  defaultExpanded,
  onUpdate,
}: {
  invoice:         Invoice;
  requestId:       number;
  request?:        FoiaRequest;
  requester?:      Requester | null;
  actorEmail?:     string;
  defaultExpanded: boolean;
  onUpdate:        (updated: Invoice) => void;
}) {
  const [invoice,          setInvoice]          = useState(initialInvoice);
  const [expanded,         setExpanded]         = useState(defaultExpanded);
  const [paymentUrl,       setPaymentUrl]       = useState<string | null>(null);
  const [previewType,      setPreviewType]      = useState<'sent' | 'estimateOnly' | null>(null);
  const [showMarkPaid,     setShowMarkPaid]     = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [adding,           setAdding]           = useState(false);
  const [addingItem,       setAddingItem]       = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [waiverResolving,  setWaiverResolving]  = useState(false);
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const [newItemForm, setNewItemForm] = useState({
    type: 'copy' as InvoiceItemType,
    description: '',
    quantity: '1',
    unitPrice: '0.00',
  });

  function update(inv: Invoice) { setInvoice(inv); onUpdate(inv); }

  useEffect(() => {
    // Load existing token; if the invoice is already sent and has no token, auto-create one
    // so the customer portal always shows the Pay button.
    getPaymentTokenForInvoice(requestId, invoice.recordId)
      .then(async t => {
        if (t) { setPaymentUrl(buildPaymentUrl(t.token)); return; }
        if (invoice.status === 'sent') {
          const created = await createPaymentToken(requestId, invoice.recordId, actorEmail ?? '').catch(() => null);
          if (created) setPaymentUrl(buildPaymentUrl(created.token));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice.recordId]);

  async function handleSendConfirm(emailBody: string, doSendEmail: boolean) {
    if (!previewType) return;
    const updated = await updateInvoiceStatus(invoice.recordId, previewType, actorEmail, requestId);
    update(updated);
    // Auto-create a payment token so the public portal can show the Pay Online button
    if (previewType === 'sent') {
      try { await createPaymentToken(requestId, invoice.recordId, actorEmail ?? ''); } catch { /* non-fatal */ }
    }
    if (doSendEmail && requester?.email) {
      const label = previewType === 'estimateOnly' ? 'Fee Estimate' : 'Invoice';
      await sendCorrespondence({
        requestId,
        direction:   'outbound',
        toAddresses: [requester.email],
        subject:     `${label} for Records Request ${request?.requestNumber ?? requestId}`,
        body:        emailBody,
        isAutomated: false,
      }, actorEmail);
    }
    setPreviewType(null);
  }

  async function handleGetPaymentLink() {
    setPaymentLinkLoading(true);
    try {
      const token = await createPaymentToken(requestId, invoice.recordId, actorEmail ?? '');
      const url = buildPaymentUrl(token.token);
      setPaymentUrl(url);
      setShowPaymentModal(true);
    } finally {
      setPaymentLinkLoading(false);
    }
  }

  async function handleSendPaymentLinkEmail() {
    if (!paymentUrl || !requester?.email) return;
    const reqNum = request?.requestNumber ?? String(requestId);
    await sendCorrespondence({
      requestId,
      direction:   'outbound',
      toAddresses: [requester.email],
      subject:     `Payment Link — Records Request ${reqNum}`,
      body: [
        `Dear ${requester.displayName ?? 'Requester'},`,
        '',
        `Your invoice for public records request #${reqNum} is ready for payment.`,
        '',
        `Amount due: ${formatMoney(invoice.total)}`,
        '',
        `Pay online securely using the link below:`,
        paymentUrl,
        '',
        `This link expires in 30 days. If you have questions, please contact us at records@example.gov.`,
        '',
        'Thank you,\nRecords Office',
      ].join('\n'),
      isAutomated: false,
    }, actorEmail);
    setShowPaymentModal(false);
  }

  async function handleVoid() {
    setSaving(true);
    try { update(await updateInvoiceStatus(invoice.recordId, 'void', actorEmail, requestId)); }
    finally { setSaving(false); }
  }

  async function handleMarkPaidConfirm() {
    update(await updateInvoiceStatus(invoice.recordId, 'paid', actorEmail, requestId));
    setShowMarkPaid(false);
  }

  async function handleWaiverDecision(decision: 'approved' | 'denied') {
    setWaiverResolving(true);
    try { update(await resolveFeeWaiver(invoice.recordId, decision, invoice.notes, actorEmail, requestId)); }
    finally { setWaiverResolving(false); }
  }

  async function handleAddItem() {
    if (!newItemForm.description.trim()) return;
    setAddingItem(true);
    try {
      const qty = parseFloat(newItemForm.quantity) || 1;
      const up  = parseFloat(newItemForm.unitPrice) || 0;
      const item = await addInvoiceItem(invoice.recordId, {
        type:        newItemForm.type,
        description: newItemForm.description.trim(),
        quantity:    qty,
        unitPrice:   up,
      });
      const updatedItems = [...invoice.items, item];
      await recalcInvoice(invoice.recordId, updatedItems);
      const newTotal = parseFloat(updatedItems.reduce((s, i) => s + i.total, 0).toFixed(2));
      update({ ...invoice, items: updatedItems, total: newTotal, subtotal: newTotal });
      setNewItemForm({ type: 'copy', description: '', quantity: '1', unitPrice: '0.00' });
      setAdding(false);
    } finally {
      setAddingItem(false);
    }
  }

  const s = STATUS_STYLE[invoice.status];

  return (
    <>
      {/* Modals */}
      {previewType && (
        <SendPreviewModal invoice={invoice} sendType={previewType} request={request} requester={requester} actorEmail={actorEmail} onConfirm={handleSendConfirm} onClose={() => setPreviewType(null)} />
      )}
      {showMarkPaid && (
        <MarkPaidModal invoice={invoice} onConfirm={handleMarkPaidConfirm} onClose={() => setShowMarkPaid(false)} />
      )}
      {showPaymentModal && paymentUrl && (
        <PaymentLinkModal invoice={invoice} paymentUrl={paymentUrl} requester={requester} request={request} onSendEmail={handleSendPaymentLinkEmail} onClose={() => setShowPaymentModal(false)} />
      )}

      <div style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'hidden' }}>
        {/* Card header — always visible */}
        <div
          onClick={() => setExpanded(e => !e)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '11px 14px', cursor: 'pointer', userSelect: 'none',
            background: expanded ? 'var(--color-neutral-50)' : 'white',
            borderBottom: expanded ? '1px solid var(--color-neutral-100)' : 'none',
            transition: 'background 0.1s',
          }}
        >
          <IconReceipt size={14} style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-800)' }}>
            Invoice #{invoice.recordId}
          </span>
          <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: s.bg, color: s.text }}>
            {s.label}
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-neutral-700)', marginLeft: 'auto' }}>
            {formatMoney(invoice.total)}
          </span>
          {invoice.invoiceSentAt && (
            <span style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>
              Sent {formatDate(invoice.invoiceSentAt)}
            </span>
          )}
          {invoice.paidAt && (
            <span style={{ fontSize: 12, color: 'var(--cds-green-700)' }}>
              Paid {formatDate(invoice.paidAt)}
            </span>
          )}
          {expanded ? <IconChevronUp size={14} style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }} /> : <IconChevronDown size={14} style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }} />}
        </div>

        {/* Card body — expanded */}
        {expanded && (
          <div style={{ padding: '14px 16px' }}>

            {/* Fee waiver banners */}
            {invoice.feeWaiverStatus === 'pending' && (
              <div style={{ marginBottom: 14, padding: '12px 14px', background: 'var(--cds-yellow-50)', border: '1px solid #FED7AA', borderRadius: 4 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <IconAlertTriangle size={16} style={{ color: 'var(--cds-yellow-500)', flexShrink: 0, marginTop: 1 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--cds-terracotta-800)', marginBottom: 2 }}>Fee Waiver Requested</div>
                    {invoice.feeWaiverReason && <div style={{ fontSize: 12, color: 'var(--cds-terracotta-700)', marginBottom: 8 }}>{invoice.feeWaiverReason}</div>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary btn-sm" style={{ gap: 4, background: 'var(--cds-green-700)', borderColor: 'var(--cds-green-700)' }} disabled={waiverResolving} onClick={() => handleWaiverDecision('approved')}>
                        {waiverResolving ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <IconCheck size={11} />} Approve Waiver
                      </button>
                      <button className="btn btn-secondary btn-sm" style={{ color: 'var(--cds-red-700)' }} disabled={waiverResolving} onClick={() => handleWaiverDecision('denied')}>
                        Deny Waiver
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {invoice.feeWaiverStatus === 'approved' && (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--cds-green-50)', border: '1px solid var(--cds-green-200)', borderRadius: 4, fontSize: 12, color: 'var(--cds-green-700)' }}>
                <IconCheck size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Fee waiver approved — invoice voided.
              </div>
            )}
            {invoice.feeWaiverStatus === 'denied' && (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--cds-red-50)', border: '1px solid #F7D5D0', borderRadius: 4, fontSize: 12, color: 'var(--cds-red-700)' }}>
                Fee waiver denied. Original fees apply.
              </div>
            )}

            {invoice.notes && (
              <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--color-neutral-500)', fontStyle: 'italic' }}>{invoice.notes}</div>
            )}

            {/* Line items */}
            <div style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-neutral-50)' }}>
                    {['Item', 'Qty', 'Unit Price', 'Total'].map((h, i) => (
                      <th key={h} style={{ padding: '7px 10px', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-neutral-500)', textAlign: i === 0 ? 'left' : 'right', borderBottom: '1px solid var(--color-neutral-100)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: '20px', textAlign: 'center', fontSize: 14, color: 'var(--color-neutral-400)' }}>No line items yet.</td></tr>
                  ) : invoice.items.map(item => {
                    const type = ITEM_TYPES.find(t => t.value === item.type);
                    return (
                      <tr key={item.recordId} style={{ borderTop: '1px solid var(--color-neutral-100)' }}>
                        <td style={{ padding: '8px 10px', fontSize: 14, color: 'var(--color-neutral-700)' }}>
                          <div>{item.description}</div>
                          <div style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>{type?.label}</div>
                        </td>
                        <td style={{ padding: '8px 10px', fontSize: 14, textAlign: 'right', color: 'var(--color-neutral-700)' }}>{item.quantity}</td>
                        <td style={{ padding: '8px 10px', fontSize: 14, textAlign: 'right', color: 'var(--color-neutral-700)' }}>{formatMoney(item.unitPrice)}</td>
                        <td style={{ padding: '8px 10px', fontSize: 14, textAlign: 'right', fontWeight: 600, color: 'var(--color-neutral-800)' }}>{formatMoney(item.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {invoice.items.length > 0 && (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--color-neutral-200)' }}>
                      <td colSpan={3} style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-600)' }}>Total</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 14, fontWeight: 700, color: 'var(--color-neutral-900)' }}>{formatMoney(invoice.total)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* Add line item form */}
            {invoice.status === 'draft' && (
              adding ? (
                <div style={{ background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-200)', borderRadius: 4, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 70px 90px', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Type</label>
                      <select className="form-input" style={{ fontSize: 12 }} value={newItemForm.type} onChange={e => setNewItemForm(f => ({ ...f, type: e.target.value as InvoiceItemType }))}>
                        {ITEM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Description *</label>
                      <input className="form-input" style={{ fontSize: 12 }} placeholder="e.g. 250 pages @ $0.10" value={newItemForm.description} onChange={e => setNewItemForm(f => ({ ...f, description: e.target.value }))} autoFocus />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Qty</label>
                      <input className="form-input" style={{ fontSize: 12 }} type="number" min="0" step="any" value={newItemForm.quantity} onChange={e => setNewItemForm(f => ({ ...f, quantity: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Unit Price</label>
                      <input className="form-input" style={{ fontSize: 12 }} type="number" min="0" step="0.01" value={newItemForm.unitPrice} onChange={e => setNewItemForm(f => ({ ...f, unitPrice: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setAdding(false)}>Cancel</button>
                    <button className="btn btn-primary btn-sm" disabled={!newItemForm.description.trim() || addingItem} onClick={handleAddItem}>
                      {addingItem ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Add Line'}
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn btn-secondary btn-sm" style={{ gap: 4, marginBottom: 10 }} onClick={() => setAdding(true)}>
                  <IconPlus size={12} /> Add Line Item
                </button>
              )
            )}

            {/* Action row */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {invoice.status === 'draft' && (
                <>
                  <button className="btn btn-secondary btn-sm" disabled={saving || invoice.items.length === 0} onClick={() => setPreviewType('estimateOnly')}>Send Estimate…</button>
                  <button className="btn btn-primary btn-sm" disabled={saving || invoice.items.length === 0} onClick={() => setPreviewType('sent')}>Send Invoice…</button>
                </>
              )}
              {invoice.status === 'sent' && (
                <>
                  <button className="btn btn-secondary btn-sm" style={{ gap: 4 }} disabled={paymentLinkLoading} onClick={paymentUrl ? () => setShowPaymentModal(true) : handleGetPaymentLink}>
                    {paymentLinkLoading ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconLink size={12} /> {paymentUrl ? 'Payment Link' : 'Get Payment Link'}</>}
                  </button>
                  <button className="btn btn-primary btn-sm" style={{ gap: 4 }} disabled={saving} onClick={() => setShowMarkPaid(true)}>
                    <IconCheck size={12} /> Mark Paid…
                  </button>
                </>
              )}
              {invoice.status !== 'paid' && invoice.status !== 'void' && (
                <button className="btn btn-secondary btn-sm" style={{ color: 'var(--cds-red-700)', marginLeft: 'auto' }} disabled={saving} onClick={handleVoid}>Void</button>
              )}
            </div>

            {/* Payment receipt */}
            {invoice.status === 'paid' && invoice.paidAt && (
              <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--cds-green-50)', border: '1px solid var(--cds-green-200)', borderRadius: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <IconCheck size={14} style={{ color: 'var(--cds-green-700)' }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--cds-green-700)' }}>Payment Received</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 12 }}>
                  {[
                    ['Date', formatDate(invoice.paidAt)],
                    ['Amount', formatMoney(invoice.total)],
                    ...(invoice.paymentMethod   ? [['Method',    invoice.paymentMethod]]   : []),
                    ...(invoice.stripePaymentId ? [['Reference', invoice.stripePaymentId]] : []),
                  ].map(([label, value]) => (
                    <div key={label}><span style={{ color: 'var(--cds-green-700)', opacity: 0.7 }}>{label}: </span><span style={{ color: '#14532D', fontWeight: 600 }}>{value}</span></div>
                  ))}
                </div>
              </div>
            )}

            {/* Active payment link banner */}
            {invoice.status !== 'paid' && paymentUrl && (
              <div style={{ marginTop: 10, padding: '9px 12px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--cds-blurple-700)' }}>
                <IconLink size={13} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>Payment link active.</span>
                <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, gap: 3 }} onClick={() => setShowPaymentModal(true)}>
                  <IconCopy size={11} /> View Link
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────

export default function InvoicesTab({ requestId, requesterId, actorEmail, request, requester }: Props) {
  const [invoices, setInvoices] = useState<Invoice[] | undefined>(undefined);
  const [creating, setCreating] = useState(false);

  function loadInvoices() {
    listInvoicesForRequest(requestId).then(setInvoices).catch(() => setInvoices([]));
  }

  useEffect(() => {
    loadInvoices();
  }, [requestId]);

  // Re-fetch when a payment or other invoice change comes in via realtime
  useEffect(() => {
    function handleDataChange(e: Event) {
      const { tables } = (e as CustomEvent).detail ?? {};
      if (Array.isArray(tables) && tables.includes('i1sy9y14o')) loadInvoices();
    }
    window.addEventListener('foia:datachange', handleDataChange);
    return () => window.removeEventListener('foia:datachange', handleDataChange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  async function handleCreateInvoice() {
    setCreating(true);
    try {
      const inv = await createInvoice({ requestId, requesterId, items: [] }, actorEmail);
      setInvoices(prev => [inv, ...(prev ?? [])]);
    } finally {
      setCreating(false);
    }
  }

  function handleUpdate(updated: Invoice) {
    setInvoices(prev => prev?.map(inv => inv.recordId === updated.recordId ? updated : inv) ?? []);
  }

  if (invoices === undefined) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-400)' }}><IconLoader2 size={22} style={{ animation: 'spin 1s linear infinite' }} /></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {invoices.length === 0 && (
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <IconReceipt size={32} style={{ color: 'var(--color-neutral-300)', marginBottom: 16 }} />
          <div style={{ fontSize: 14, color: 'var(--color-neutral-500)', marginBottom: 16 }}>
            No invoices for this request yet.
          </div>
        </div>
      )}

      {invoices.map((inv, i) => (
        <InvoiceCard
          key={inv.recordId}
          invoice={inv}
          requestId={requestId}
          request={request}
          requester={requester}
          actorEmail={actorEmail}
          defaultExpanded={i === 0}
          onUpdate={handleUpdate}
        />
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: invoices.length === 0 ? 0 : 4 }}>
        <button className="btn btn-secondary btn-sm" style={{ gap: 4 }} disabled={creating} onClick={handleCreateInvoice}>
          {creating ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconPlus size={12} /> New Invoice</>}
        </button>
      </div>
    </div>
  );
}
